'use strict';
/*
  main.js — the webapp entry point. Reuses the EXACT engine code: it imports the
  real styles/ registry and lib/captions.js (esbuild aliases lib/draw -> the
  browser toolkit). It parses a transcript (or calls the Whisper Worker), runs
  optional AI cleanup/translation via the Workers, builds cues, and drives Player.
*/
import styleReg from '../../styles/index.js';
import caps from '../../lib/captions.js';
import { Player } from './player.js';
import { toShareableMp4 } from './mp4.js';
import { APP, afetch, setOnExpired, getStoredToken, loginWithPassword, signupWithPassword, requestPasswordReset, signOut, consumeReturn } from './auth.js';

const $ = id => document.getElementById(id);
// iOS/iPadOS/Android. (iPadOS reports as "Mac" but has touch points.)
const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Mac/i.test(navigator.userAgent));
const status = (m, cls) => { const el = $('status'); el.textContent = m; el.className = 'status ' + (cls || ''); };

// ---- transcript parsing (port of lib/transcribe.js loaders) ----
function parseSRT(txt) {
  const blocks = txt.replace(/\r/g, '').split(/\n\n+/), segs = [];
  const toSec = ts => { const m = ts.match(/(\d+):(\d+):(\d+)[,.](\d+)/); return m ? (+m[1] * 3600 + +m[2] * 60 + +m[3] + +m[4] / 1000) : 0; };
  for (const b of blocks) {
    const lines = b.split('\n').filter(Boolean), tl = lines.find(l => l.includes('-->'));
    if (!tl) continue;
    const [a, b2] = tl.split('-->').map(s => s.trim());
    const text = lines.slice(lines.indexOf(tl) + 1).join(' ').trim();
    if (text) segs.push({ start: toSec(a), end: toSec(b2), text, words: [] });
  }
  return segs;
}
function loadTranscript(name, raw) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'json') {
    const j = JSON.parse(raw);
    if (Array.isArray(j) && j.length && (j[0].start !== undefined || j[0].words)) return j;
    if (Array.isArray(j) && j.length && j[0].s !== undefined) return [{ start: j[0].s, end: j[j.length - 1].e, text: j.map(w => w.w).join(' '), words: j }];
    if (j.segments) return j.segments;
    throw new Error('Unrecognised JSON transcript shape');
  }
  if (ext === 'srt' || ext === 'vtt') return parseSRT(raw);
  return [{ start: 0, end: 0, text: raw.trim(), words: [] }];
}
function toSrtTime(t) {
  t = Math.max(0, t || 0);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = Math.floor(t % 60), ms = Math.round((t - Math.floor(t)) * 1000);
  const p = (n, l = 2) => String(n).padStart(l, '0');
  return `${p(h)}:${p(m)}:${p(s)},${p(ms, 3)}`;
}
function segmentsToSrt(segs) {
  return (segs || []).filter(s => (s.text || '').trim()).map((s, i) => `${i + 1}\n${toSrtTime(s.start)} --> ${toSrtTime(s.end)}\n${s.text.trim()}`).join('\n\n');
}
const looksLikeSrt = txt => /\d+:\d+:\d+[,.]\d+\s*-->/.test(txt);

// ---- replicate forge.js cueLayout (per-style anims / casing) ----
function cueLayout(style, mode) {
  const id = style.id;
  const anims = id === 'utypisk' ? ['slam', 'drop', 'punch', 'split', 'swing', 'zoom']
    : id === 'linger' ? ['rise', 'pop', 'spring', 'rise', 'pop', 'spring']
    : id === 'tape' ? ['type', 'type', 'type']
    : id === 'brat' ? ['rise', 'pop', 'rise', 'spring']
    : id === 'kraft' ? ['drop', 'wobble', 'swing', 'rise', 'pop']
    : id === 'pop' ? ['pop', 'slam', 'punch', 'pop', 'zoom']
    : id === 'chrome' ? ['zoom', 'spring', 'rise', 'pop', 'swing']
    : ['zoom', 'spring', 'pop', 'swing', 'slam', 'rise'];
  return { anims, upper: !(id === 'brat' || id === 'tape'), maxCharsPerLine: 16, maxLines: 3, maxWords: mode === 'talk' ? 5 : 6 };
}

async function api(path, body) {
  const r = await afetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(path + ' -> ' + r.status + ' ' + (await r.text()).slice(0, 200));
  return r.json();
}

const state = { videoEl: null, videoURL: null, fileName: 'reel' };

// ---- credits. Transcribe/proofread/translate are free; you only spend when you
//      render. Mirrors the server's _credits.js PRICE (must stay in sync).
const CR = { render: 5, dub: 20 };
let credits = null;       // current balance (null until fetched)
let unlimited = false;    // owner accounts render for free
function renderCost() { return CR.render + (($('dub') && $('dub').checked) ? CR.dub : 0); }
function updateCost() {
  const el = $('costEst'); if (!el) return;
  if (unlimited) { el.textContent = 'Unlimited credits — render freely.'; return; }
  const dub = !!($('dub') && $('dub').checked);
  const have = (credits == null) ? '' : ' · you have ' + credits;
  el.textContent = 'This render: ' + renderCost() + ' credits' + (dub ? ' (incl. voice dub)' : '') + have;
}
function setCredits(n) {
  if (n === 'unlimited') { unlimited = true; }
  else if (typeof n === 'number') { credits = n; unlimited = false; }
  const c = $('acctCredits'); if (c) c.textContent = unlimited ? '∞ credits' : (credits == null ? '' : credits + ' credits');
  const buy = $('buyBtn'); if (buy) buy.style.display = unlimited ? 'none' : '';
  updateCost();
}
async function refreshCredits() {
  try { const r = await afetch('/api/credits/balance'); if (r.ok) { const j = await r.json(); setCredits(j.unlimited ? 'unlimited' : j.balance); } } catch (e) {}
}
function openBuy(note) {
  const m = $('buyModal'); if (!m) return;
  const b = $('buyBalance'); if (b) b.textContent = note || ('You have ' + (credits == null ? 0 : credits) + ' credits.');
  m.style.display = 'flex';
}
function closeBuy() { const m = $('buyModal'); if (m) m.style.display = 'none'; }
async function startCheckout() {
  const btn = $('buyGo'); const prev = btn ? btn.textContent : '';
  if (btn) { btn.disabled = true; btn.textContent = 'Redirecting…'; }
  try {
    const r = await afetch('/api/credits/checkout', { method: 'POST' });
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.url) { location.href = j.url; return; }
    const b = $('buyBalance'); if (b) b.textContent = j.error || 'Checkout is unavailable right now.';
  } catch (e) { const b = $('buyBalance'); if (b) b.textContent = 'Checkout failed — please try again.'; }
  finally { if (btn) { btn.disabled = false; btn.textContent = prev; } }
}
function wireCredits() {
  const b = $('buyBtn'); if (b) b.addEventListener('click', () => openBuy());
  const x = $('buyClose'); if (x) x.addEventListener('click', closeBuy);
  const g = $('buyGo'); if (g) g.addEventListener('click', startCheckout);
  const m = $('buyModal'); if (m) m.addEventListener('click', e => { if (e.target === m) closeBuy(); });
  const p = new URLSearchParams(location.search);
  if (p.get('bought') === '1') { status('Payment received — credits added ✓', 'ok'); }
  if (p.get('bought') != null) history.replaceState({}, '', location.pathname);
}

function readFile(file) { return new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsText(file); }); }

function populateStyles() {
  const sel = $('style');
  for (const s of styleReg.list()) { const o = document.createElement('option'); o.value = s.id; o.textContent = s.label; sel.appendChild(o); }
  sel.value = 'linger';
}

// Language dropdowns so users pick a name, not a code (codes still passed to the Workers).
const LANGS = [
  ['da', 'Danish'], ['en', 'English'], ['de', 'German'], ['sv', 'Swedish'], ['no', 'Norwegian'],
  ['nl', 'Dutch'], ['es', 'Spanish'], ['fr', 'French'], ['it', 'Italian'], ['pt', 'Portuguese'],
  ['pl', 'Polish'], ['fi', 'Finnish'], ['is', 'Icelandic'], ['ja', 'Japanese'], ['ko', 'Korean'],
  ['zh', 'Chinese'], ['ar', 'Arabic'], ['ru', 'Russian'], ['tr', 'Turkish'], ['hi', 'Hindi'],
];
function fillLangSelect(sel, blankLabel, def) {
  if (!sel) return;
  const blank = document.createElement('option'); blank.value = ''; blank.textContent = blankLabel; sel.appendChild(blank);
  for (const [code, name] of LANGS) { const o = document.createElement('option'); o.value = code; o.textContent = name; sel.appendChild(o); }
  if (def != null) sel.value = def;
}
function populateLangs() {
  fillLangSelect($('lang'), 'Auto-detect', 'da');        // spoken language (hint for ASR / translate source)
  fillLangSelect($('translate'), 'Keep spoken (no translation)', ''); // subtitle target
}

async function loadVideo(file) {
  if (state.videoURL) URL.revokeObjectURL(state.videoURL);
  state.videoURL = URL.createObjectURL(file); state.fileName = file.name.replace(/\.[^.]+$/, '');
  const v = $('srcVideo'); v.src = state.videoURL;
  await new Promise((res, rej) => { v.onloadedmetadata = res; v.onerror = () => rej(new Error('Could not load video')); });
  state.videoEl = v;
  status('Video loaded · ' + Math.round(v.duration) + 's · ' + v.videoWidth + '×' + v.videoHeight, 'ok');
  updateCost();
}

// ---- browser-side audio prep for Whisper: decode → mono 16 kHz → WAV, chunked.
// Sending the raw video (tens of MB) overruns Workers AI; a 16 kHz mono WAV is tiny.
function encodeWav(samples, sr) {
  const buf = new ArrayBuffer(44 + samples.length * 2), v = new DataView(buf);
  const wr = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  wr(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); wr(8, 'WAVE'); wr(12, 'fmt ');
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  wr(36, 'data'); v.setUint32(40, samples.length * 2, true);
  let o = 44; for (let i = 0; i < samples.length; i++, o += 2) { const s = Math.max(-1, Math.min(1, samples[i])); v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true); }
  return new Blob([v], { type: 'audio/wav' });
}
async function audioToWavChunks(file, sr = 16000, chunkSec = 28) {
  const ab = await file.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const tmp = new AC(); let decoded;
  try { decoded = await tmp.decodeAudioData(ab); } finally { try { tmp.close(); } catch (e) {} }
  const off = new OfflineAudioContext(1, Math.ceil(decoded.duration * sr), sr);
  const src = off.createBufferSource(); src.buffer = decoded; src.connect(off.destination); src.start();
  const data = (await off.startRendering()).getChannelData(0);
  const n = chunkSec * sr, out = [];
  for (let i = 0; i < data.length; i += n) {
    const slice = data.subarray(i, Math.min(data.length, i + n));
    out.push({ blob: encodeWav(slice, sr), offset: i / sr, dur: slice.length / sr });
  }
  return out;
}

// Real-time audio capture from a media element — handles video containers (.mov/.mp4)
// that decodeAudioData can't. Mirrors player.js's render-audio path: route the element
// through WebAudio to a MediaStreamDestination (NOT to the speakers → silent) and record
// it with MediaRecorder. Workers AI Whisper accepts the resulting webm/mp4 audio.
async function captureAudioFromMedia(file) {
  const url = URL.createObjectURL(file);
  const el = document.createElement('video');
  el.src = url; el.preload = 'auto'; el.playsInline = true;
  try {
    await new Promise((res, rej) => { el.onloadedmetadata = () => res(); el.onerror = () => rej(new Error('cannot load media')); });
    const AC = window.AudioContext || window.webkitAudioContext;
    const ac = new AC();
    let srcNode;
    try { srcNode = ac.createMediaElementSource(el); }
    catch (e) { try { await ac.close(); } catch (_) {} throw new Error('audio capture unsupported in this browser'); }
    const dest = ac.createMediaStreamDestination();
    srcNode.connect(dest); // not connected to ac.destination → no sound to the user
    const mimes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    let mime = ''; for (const m of mimes) if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
    const rec = new MediaRecorder(dest.stream, mime ? { mimeType: mime } : undefined);
    const parts = []; rec.ondataavailable = e => { if (e.data && e.data.size) parts.push(e.data); };
    const stopped = new Promise(res => { rec.onstop = res; });
    rec.start();
    try { await ac.resume(); } catch (e) {}
    await el.play();
    await new Promise(res => { el.onended = res; });
    rec.stop(); await stopped;
    try { srcNode.disconnect(); } catch (e) {}
    try { await ac.close(); } catch (e) {}
    const blob = new Blob(parts, { type: rec.mimeType || mime || 'audio/webm' });
    if (!blob.size) throw new Error('no audio captured (the clip may have no audio track)');
    return blob;
  } finally { URL.revokeObjectURL(url); }
}

// → a small audio Blob Whisper can read. Real audio files decode straight to WAV;
//   video containers fall back to real-time capture.
async function fileToAudioBlob(file) {
  try { const c = await audioToWavChunks(file, 16000, 1e9); if (c.length && c[0].blob.size > 44) return c[0].blob; } catch (e) {}
  return captureAudioFromMedia(file);
}

// transcript file, or transcribe via Worker. (The editable caption box is handled
// in buildCues so it can skip re-proofreading the user's edits.)
async function getSegments() {
  const tf = $('transcript').files[0];
  if (tf) return loadTranscript(tf.name, await readFile(tf));
  const af = $('audioForASR').files[0] || $('video').files[0];
  if (!af) throw new Error('Provide a transcript (.srt/.json/.txt), paste/transcribe caption text, or pick a file.');
  let audioBlob;
  try {
    status('Extracting audio…');
    audioBlob = await fileToAudioBlob(af);
  } catch (e) {
    throw new Error('Could not get audio from that file (' + e.message + '). Upload an .mp3/.wav/.m4a, or supply a transcript instead.');
  }
  status('Transcribing with Whisper (Workers AI)…');
  const fd = new FormData(); fd.append('audio', audioBlob, 'audio');
  const r = await afetch('/api/transcribe', { method: 'POST', body: fd });
  if (!r.ok) throw new Error('Transcribe failed: ' + r.status + ' ' + (await r.text()).slice(0, 160));
  const j = await r.json();
  return j.segments || [];
}

// "Transcribe now" — decode the speech and drop it into the editable caption box,
// so the user can fix any misheard words before rendering (a simple caption editor).
// local status shown right under the Transcribe button (not the far-away render status)
function tstatus(msg, cls) {
  const el = $('transcribeHint'); if (!el) return;
  el.textContent = msg;
  el.style.color = cls === 'err' ? '#ff7a7a' : (cls === 'ok' ? '#7bd88f' : '#d8d6df');
}

async function transcribeNow() {
  const btn = $('transcribeBtn');
  const af = $('audioForASR').files[0] || $('video').files[0];
  if (!af) { tstatus('Load a talking-head video (or an audio file) first.', 'err'); return; }
  if (btn) btn.disabled = true;
  try {
    tstatus('Extracting audio…');
    const blob = await fileToAudioBlob(af);
    tstatus('Transcribing with Whisper (Workers AI)…');
    const fd = new FormData(); fd.append('audio', blob, 'audio');
    const r = await afetch('/api/transcribe', { method: 'POST', body: fd });
    if (!r.ok) throw new Error('Transcribe failed: ' + r.status + ' ' + (await r.text()).slice(0, 160));
    const j = await r.json();
    const segs = j.segments || [];
    if (!segs.some(s => (s.text || '').trim())) throw new Error('No speech detected in the audio.');
    const hasTime = segs.some(s => (s.end || 0) > 0);
    $('captionsText').value = hasTime ? segmentsToSrt(segs) : segs.map(s => s.text).join(' ').replace(/\s+/g, ' ').trim();
    try { $('captionsText').focus(); } catch (e) {}
    updateCost();
    tstatus(hasTime ? 'Done ✓ — edit the SRT in the box above, then Render.' : 'Done ✓ — edit the text above, then Render.', 'ok');
  } catch (e) {
    console.error(e);
    tstatus('Transcribe failed: ' + e.message, 'err');
  } finally { if (btn) btn.disabled = false; }
}

// local status under the proofread button
function pstatus(msg, cls) {
  const el = $('proofreadHint'); if (!el) return;
  el.textContent = msg;
  el.style.color = cls === 'err' ? '#ff7a7a' : (cls === 'ok' ? '#7bd88f' : '#d8d6df');
}

// "AI-proofread" — fix misheard words in the caption box in place (Claude), keeping
// timestamps. The user reviews the result afterwards; this never auto-runs at render.
async function proofreadNow() {
  const btn = $('proofreadBtn');
  const boxText = $('captionsText').value.trim();
  if (!boxText) { pstatus('Transcribe or paste captions first.', 'err'); return; }
  const isSrt = looksLikeSrt(boxText);
  let segments = isSrt ? parseSRT(boxText) : [{ start: 0, end: 0, text: boxText, words: [] }];
  if (!segments.length) { pstatus('Nothing to proofread.', 'err'); return; }
  if (btn) btn.disabled = true;
  try {
    pstatus('Proofreading misheard words (Claude)…');
    const j = await api('/api/clean', { segments, lang: $('lang').value || null, context: $('context').value || null });
    const cleaned = j.segments || segments;
    $('captionsText').value = isSrt
      ? segmentsToSrt(cleaned)
      : cleaned.map(s => s.text).join(' ').replace(/\s+/g, ' ').trim();
    pstatus('Proofread ✓ — review the box above, then Render.', 'ok');
  } catch (e) {
    console.error(e);
    pstatus('Proofread failed: ' + e.message, 'err');
  } finally { if (btn) btn.disabled = false; }
}

// the hero phone shows ONE of: the style sample, the live render canvas, or the result
// — and the caption beneath it follows that state so it never reads "stale".
function heroShow(id) {
  ['stylePreview', 'stage', 'result'].forEach(x => { const e = $(x); if (e) e.classList.toggle('show', x === id); });
  const h = $('herohint'); if (!h) return;
  h.textContent = id === 'result' ? 'Your reel is ready — download or save it below.'
    : id === 'stage' ? 'Rendering your reel in real time…'
    : 'Live sample of your caption style — add a clip & captions, then Render.';
}

// Render a single static frame of the selected style onto the small preview canvas,
// using the REAL engine (Player) with a synthetic caption — so the sample matches
// exactly what renders. No video → talk mode fills the style's bg and overlays text.
function renderStylePreview() {
  const cv = $('stylePreview'); if (!cv) return;
  try {
    const style = styleReg.get($('style').value);
    const dur = 6;
    const cues = caps.fromPlainText('Captions that actually pop', dur, cueLayout(style, 'talk'));
    const p = new Player({ canvas: cv, video: null, style, cues, fps: 30, mode: 'talk', dur, watermark: null });
    // a beat into the first cue, so reveal animations have settled
    const t = (cues[0] ? cues[0].t : 0) + 1.0;
    p.drawAt(t);
    heroShow('stylePreview'); // switch the hero to the style sample (e.g. on style change)
  } catch (e) { console.warn('style preview failed:', e.message); }
}

// local status under the translate button
function xstatus(msg, cls) {
  const el = $('translateHint'); if (!el) return;
  el.textContent = msg;
  el.style.color = cls === 'err' ? '#ff7a7a' : (cls === 'ok' ? '#7bd88f' : '#d8d6df');
}

// "Translate subtitles" — render the caption box into the subtitle language (Claude),
// in place, keeping timestamps. Like proofread, this is explicit so the user can edit
// the translation before rendering (render never auto-translates).
async function translateNow() {
  const btn = $('translateBtn');
  const to = $('translate').value.trim();
  if (!to) { xstatus('Enter a subtitle language above first (e.g. en).', 'err'); return; }
  const boxText = $('captionsText').value.trim();
  if (!boxText) { xstatus('Transcribe or paste captions first.', 'err'); return; }
  const isSrt = looksLikeSrt(boxText);
  let segments = isSrt ? parseSRT(boxText) : [{ start: 0, end: 0, text: boxText, words: [] }];
  if (!segments.length) { xstatus('Nothing to translate.', 'err'); return; }
  if (btn) btn.disabled = true;
  try {
    xstatus('Translating → ' + to + ' (Claude)…');
    const j = await api('/api/translate', { segments, to, from: $('lang').value || null });
    const tr = j.segments || segments;
    // write into the SEPARATE translated box — the original captions stay untouched
    const wrap = $('translatedWrap'); if (wrap) wrap.style.display = '';
    $('translatedText').value = isSrt
      ? segmentsToSrt(tr)
      : tr.map(s => s.text).join(' ').replace(/\s+/g, ' ').trim();
    try { $('translatedText').focus(); } catch (e) {}
    updateCost();
    xstatus('Translated → ' + to + ' ✓ — review the translated box below, then Render.', 'ok');
  } catch (e) {
    console.error(e);
    xstatus('Translate failed: ' + e.message, 'err');
  } finally { if (btn) btn.disabled = false; }
}

// The segments that will actually be rendered (translated box wins over original).
// Returns null when neither box has content. Used for dubbing (needs text + timing).
function currentSegments(dur) {
  const transText = ($('translatedText') && $('translatedText').value.trim()) || '';
  const boxText = transText || $('captionsText').value.trim();
  if (!boxText) return null;
  let segs = looksLikeSrt(boxText) ? parseSRT(boxText) : null;
  if (!segs || !segs.length) segs = [{ start: 0, end: dur || 0, text: boxText, words: [] }];
  return segs;
}

// Build a dub track: one TTS clip per subtitle segment (your cloned ElevenLabs voice
// in the subtitle language), decoded to AudioBuffers tagged with each segment's start.
async function makeDub(segments, dur) {
  const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  try { await audioCtx.resume(); } catch (e) {}
  const lang = ($('translate').value.trim() || $('lang').value.trim() || '') || null;
  const segs = segments.filter(s => (s.text || '').trim());
  const buffers = [];
  for (let i = 0; i < segs.length; i++) {
    status('Dubbing voice… ' + (i + 1) + '/' + segs.length);
    const r = await afetch('/api/tts', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text: segs[i].text.trim(), lang }) });
    if (!r.ok) throw new Error('TTS failed: ' + r.status + ' ' + (await r.text()).slice(0, 160));
    const ab = await r.arrayBuffer();
    const buffer = await audioCtx.decodeAudioData(ab);
    buffers.push({ buffer, start: segs[i].start || 0 });
  }
  return { audioCtx, buffers };
}

async function buildCues(style, mode, dur) {
  // the translated box wins when it has content, else the original caption box.
  const transText = ($('translatedText') && $('translatedText').value.trim()) || '';
  const boxText = transText || $('captionsText').value.trim();
  let segments;
  if (boxText) {
    // the editable caption box is the source of truth when it has content (SRT or plain).
    segments = looksLikeSrt(boxText) ? parseSRT(boxText) : null;
    if (!segments || !segments.length) segments = [{ start: 0, end: dur || 0, text: boxText, words: [] }];
  } else {
    segments = await getSegments(); // transcript file, or transcribe-during-render
  }
  if (!segments || !segments.length) throw new Error('Provide captions, paste/transcribe text, or pick a file.');
  // Proofread + Translate are now explicit buttons (proofreadNow / translateNow) the
  // user runs on the box before rendering — never auto-run here, so the box is rendered
  // exactly as shown and manual edits/translations are always preserved.
  const words = caps.wordsFromSegments(segments);
  if (!words.length) throw new Error('No words found in transcript.');
  // Workers AI Whisper returns text without word timings → spread it across the
  // clip duration (same fallback as the CLI's cuesFromSegments).
  const hasTiming = words.some(w => (w.e || 0) > 0);
  if (!hasTiming) return caps.fromPlainText(words.map(w => w.w).join(' '), dur, cueLayout(style, mode));
  return caps.build(words, cueLayout(style, mode));
}

async function render() {
  let chargeId = null, rendered = false; // for refund-on-failure
  try {
    $('render').disabled = true; $('bar').style.width = '0%';
    const style = styleReg.get($('style').value);
    const hasVideo = !!state.videoEl;
    const mode = hasVideo ? 'talk' : 'music';
    const [W, H] = $('res').value.split('x').map(Number);
    const fps = +$('fps').value || 30;
    const dur = hasVideo ? state.videoEl.duration : (+$('musicDur').value || 15);
    await document.fonts.ready;
    status('Building captions…');
    const cues = await buildCues(style, mode, dur);
    if (!cues.length) throw new Error('No caption cues produced.');

    const wantDub = !!($('dub') && $('dub').checked);
    // Charge credits up-front (render = 5, + dub = 20). Blocks if the balance is short.
    status('Reserving credits…');
    const ch = await afetch('/api/credits/charge', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ dub: wantDub }) });
    if (ch.status === 402) {
      const j = await ch.json().catch(() => ({}));
      setCredits(j.balance);
      openBuy('This render needs ' + (j.cost || renderCost()) + ' credits — you have ' + (j.balance == null ? 0 : j.balance) + '. Top up to continue.');
      throw new Error('Not enough credits — buy a pack to render.');
    }
    if (!ch.ok) throw new Error('Could not reserve credits (' + ch.status + ').');
    { const j = await ch.json().catch(() => ({})); setCredits(j.unlimited ? 'unlimited' : j.balance); chargeId = j.charge_id || null; }

    // Optional voice dub — replaces the original voice with your cloned voice speaking
    // the rendered captions (translated, if a translation is loaded).
    let dub = null;
    if (wantDub) {
      const segs = currentSegments(dur);
      if (!segs) throw new Error('Add captions (and translate, if wanted) before dubbing.');
      status('Dubbing voice…');
      dub = await makeDub(segs, dur);
    }

    const canvas = $('stage'); canvas.width = W; canvas.height = H;
    const player = new Player({ canvas, video: hasVideo ? state.videoEl : null, style, cues, fps, mode, dur, watermark: $('watermark').value || null, dub });
    player.preview(0);
    heroShow('stage'); // show the live render in the hero phone
    status('Rendering + recording in real time… (' + Math.round(dur) + 's)');
    const raw = await player.record(p => { $('bar').style.width = Math.round(p * 100) + '%'; });
    rendered = true; // we have the file — the credits were earned, no refund
    // Finalise into a Photos-importable faststart MP4 (the recorder emits a fragmented
    // MP4/WebM iOS won't save to Photos). Falls back to the raw recording on any failure.
    let blob = raw;
    // The ffmpeg.wasm finalise loads ~30 MB and holds the whole video in memory; on
    // mobile that spike right after a render makes iOS Safari reload the tab (losing
    // the result). Desktop only — mobile keeps the raw recording.
    if (!isMobile()) {
      try {
        status('Finalising MP4 for Photos…'); $('bar').style.width = '0%';
        blob = await toShareableMp4(raw, p => { $('bar').style.width = Math.round(p * 100) + '%'; });
      } catch (e) { console.warn('MP4 finalise skipped (using raw recording):', e); }
    }
    const url = URL.createObjectURL(blob);
    const out = $('result'); out.src = url;
    heroShow('result'); // the finished reel is the prize
    const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const fname = state.fileName + '_' + style.id + '.' + ext;
    const a = $('download'); a.href = url; a.download = fname; a.style.display = 'inline-block';
    a.textContent = '↓ Download ' + ext.toUpperCase();
    // Share sheet. "Save to Photos" only appears on iPhone/iPad — macOS doesn't put
    // Photos in the file share sheet, so on desktop the share sheet is AirDrop/Messages
    // and Download is how you save. Label the button honestly per platform.
    const shareBtn = $('share');
    if (shareBtn) {
      const file = new File([blob], fname, { type: blob.type || 'video/mp4' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        const iOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent));
        shareBtn.textContent = iOS ? '⤴ Save to Photos / Share' : '⤴ Share (AirDrop, Messages…)';
        shareBtn.style.display = 'inline-block';
        shareBtn.onclick = async () => {
          try { await navigator.share({ files: [file], title: 'JustPostIt reel' }); }
          catch (e) { if (e && e.name !== 'AbortError') status('Share unavailable — use Download instead.', 'err'); }
        };
      } else { shareBtn.style.display = 'none'; }
    }
    if (dub && dub.audioCtx) try { await dub.audioCtx.close(); } catch (e) {}
    status('Done — ' + (blob.size / 1e6).toFixed(1) + ' MB ' + ext.toUpperCase() + (dub ? ' · dubbed' : ''), 'ok');
  } catch (e) {
    console.error(e);
    // refund the credits if we charged but never produced a file
    if (chargeId && !rendered) {
      try {
        const r = await afetch('/api/credits/refund', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ charge_id: chargeId }) });
        const j = await r.json().catch(() => ({}));
        if (typeof j.balance === 'number') setCredits(j.balance);
        status('Render failed — ' + e.message + ' (credits refunded).', 'err');
      } catch (_) { status('Render failed: ' + e.message, 'err'); }
    } else {
      status('Error: ' + e.message, 'err');
    }
  } finally { $('render').disabled = false; }
}

function init() {
  populateStyles();
  populateLangs();
  $('video').addEventListener('change', async e => { const f = e.target.files[0]; if (f) { try { await loadVideo(f); } catch (err) { status(err.message, 'err'); } } });
  $('style').addEventListener('change', () => { renderStylePreview(); if (state.videoEl) { try { const st = styleReg.get($('style').value); const c = $('stage'); const [W, H] = $('res').value.split('x').map(Number); c.width = W; c.height = H; } catch (e) {} } });
  $('render').addEventListener('click', render);
  const tb = $('transcribeBtn'); if (tb) tb.addEventListener('click', transcribeNow);
  const pb = $('proofreadBtn'); if (pb) pb.addEventListener('click', proofreadNow);
  const xb = $('translateBtn'); if (xb) xb.addEventListener('click', translateNow);
  // live cost estimate — refresh whenever an input that affects it changes
  ['captionsText', 'translatedText', 'translate', 'lang', 'dub', 'musicDur'].forEach(id => {
    const el = $(id); if (el) el.addEventListener(el.tagName === 'TEXTAREA' || el.type === 'text' ? 'input' : 'change', updateCost);
  });
  updateCost();
  status('Ready — load a talking-head video + a transcript, pick a style, render.', 'ok');
  // draw the initial preview once the web fonts are ready (else first paint uses a fallback face)
  document.fonts.ready.then(renderStylePreview).catch(() => renderStylePreview());
}

// ───────────────────────── auth gate ─────────────────────────
let appStarted = false;
function startApp(user) {
  document.body.classList.add('signed-in');
  const gate = $('authGate'); if (gate) gate.style.display = 'none';
  const acct = $('acct'); if (acct) { acct.style.display = 'flex'; const e = $('acctEmail'); if (e) e.textContent = user.email || ''; }
  if (!appStarted) { appStarted = true; init(); wireCredits(); }
  refreshCredits();
}
function showGate(msg) {
  document.body.classList.remove('signed-in');
  const gate = $('authGate'); if (gate) gate.style.display = 'flex';
  const acct = $('acct'); if (acct) acct.style.display = 'none';
  if (msg) authError(msg);
}
function authError(msg) { const e = $('authError'); if (e) { e.textContent = msg || ''; e.style.display = msg ? 'block' : 'none'; } }

let authMode = 'login'; // 'login' | 'signup'
function setAuthMode(mode) {
  authMode = mode;
  $('authSubmit').textContent = mode === 'signup' ? 'Create account' : 'Sign in';
  $('authToggleText').textContent = mode === 'signup' ? 'Already have an account?' : 'New here?';
  $('authToggle').textContent = mode === 'signup' ? 'Sign in' : 'Create an account';
  $('authPassword').setAttribute('autocomplete', mode === 'signup' ? 'new-password' : 'current-password');
  authError('');
}

async function submitAuth() {
  const email = ($('authEmail').value || '').trim();
  const password = $('authPassword').value || '';
  if (!email || !password) { authError('Enter your email and password.'); return; }
  const btn = $('authSubmit'); btn.disabled = true;
  const prev = btn.textContent; btn.textContent = 'Please wait…';
  try {
    const fn = authMode === 'signup' ? signupWithPassword : loginWithPassword;
    const stored = await fn({ app: APP, email, password });
    startApp({ email: stored.email || email });
  } catch (e) {
    const map = {
      invalid_credentials: 'Wrong email or password.',
      account_exists: 'That account already exists — sign in instead.',
      weak_password: 'Password too weak — use at least 8 characters.',
      origin_not_allowed: 'This site isn’t authorised yet (origin not allowed).',
      network: 'Network error — try again.',
    };
    authError(map[e && e.code] || (e && e.message) || 'Sign-in failed.');
  } finally { btn.disabled = false; btn.textContent = prev; }
}

function wireAuthUI() {
  setOnExpired(() => showGate('Your session expired — please sign in again.'));
  $('authSubmit').addEventListener('click', submitAuth);
  $('authPassword').addEventListener('keydown', e => { if (e.key === 'Enter') submitAuth(); });
  $('authToggle').addEventListener('click', e => { e.preventDefault(); setAuthMode(authMode === 'login' ? 'signup' : 'login'); });
  $('authForgot').addEventListener('click', async e => {
    e.preventDefault();
    const email = ($('authEmail').value || '').trim();
    if (!email) { authError('Enter your email above first, then tap “Forgot password”.'); return; }
    try { await requestPasswordReset({ email }); authError(''); $('authForgot').textContent = 'Check your inbox ✓'; }
    catch { authError('Could not start a reset — try again.'); }
  });
  const so = $('signout');
  if (so) so.addEventListener('click', () => { signOut({ app: APP }); location.reload(); });
}

function boot() {
  wireAuthUI();
  setAuthMode('login');
  // if we returned from the SSO redirect flow, capture the token from the URL fragment
  try { consumeReturn({ app: APP }); } catch (e) {}
  const stored = getStoredToken({ app: APP });
  if (stored) startApp({ email: stored.email || '' }); else showGate();
}
document.addEventListener('DOMContentLoaded', boot);
